import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Buildings,
  CheckCircle,
  ArrowRight,
  UserCircle,
  EnvelopeSimple,
  Phone,
  ChatCircle,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIZES = ['1–10', '11–50', '51–200', '201–1.000', '> 1.000'];

export default function B2BPage() {
  const [form, setForm] = useState({
    company: '',
    contact_name: '',
    email: '',
    phone: '',
    company_size: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      setError('Bitte füll Firma, Name und E-Mail aus.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/b2b-leads`, form);
      setDone(true);
    } catch (err) {
      setError('Anfrage konnte nicht gesendet werden. Bitte kontaktiere uns direkt.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main data-testid="b2b-page">
      <Helmet>
        <title>PendelPilot für Arbeitgeber — Recruiting-Tool mit Pendel-Vorteil</title>
        <meta
          name="description"
          content="PendelPilot White-Label für Ihr Recruiting: Zeigen Sie Bewerbern direkt den echten finanziellen Vorteil Ihres Standorts. Weniger Abbrecher, bessere Conversion."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="/fuer-arbeitgeber" />
      </Helmet>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Für Arbeitgeber &amp; HR</div>
        <h1
          data-testid="b2b-h1"
          className="font-display text-5xl md:text-7xl tracking-tighter mt-2 max-w-4xl leading-[0.95]"
        >
          Recruiting-Vorteil: Zeig Bewerbern, wie <span className="italic font-medium">gut</span> dein Standort wirklich ist.
        </h1>
        <p className="mt-5 text-lg text-neutral-700 max-w-2xl leading-relaxed">
          Integrier PendelPilot als White-Label-Rechner in deine Karriere-Seite. Bewerber sehen mit einem Klick den echten Pendel-Aufwand — und den finanziellen Vorteil eines Wechsels zu dir.
        </p>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 grid-border">
          <Benefit
            num="01"
            title="Weniger Bewerbungs-Abbrecher"
            text="Bewerber, die nicht sicher sind, ob sich der Weg lohnt, brechen ab. Mit dem Rechner sehen sie es sofort — schwarz auf weiß."
          />
          <Benefit
            num="02"
            title="Standort als USP"
            text="Wenn du gut liegst oder Homeoffice anbietest, zahlt sich das direkt aus. Der Rechner macht dein Angebot vergleichbar."
          />
          <Benefit
            num="03"
            title="Datengetrieben"
            text="Anonymisiertes Reporting: welche Regionen kommen, welche Distanzen bewerben sich, wo brechen Kandidaten ab."
          />
        </div>
      </section>

      {/* Form */}
      <section id="anfrage" className="max-w-3xl mx-auto px-6 md:px-12 mt-24 scroll-mt-20">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Unverbindliche Anfrage</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-2">Interesse? Sag Hallo.</h2>
        <p className="text-neutral-700 mt-3">
          Wir sind aktuell in der frühen Umsetzungsphase und suchen 5–10 Pilotkunden zu Sonderkonditionen. Kein Verkaufsdruck — ich melde mich persönlich zurück.
        </p>

        {done ? (
          <div
            data-testid="b2b-success"
            className="mt-8 p-8 md:p-12 grid-border bg-[#2A9D8F] text-white"
          >
            <CheckCircle size={40} weight="bold" />
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter mt-4">Danke — ist angekommen.</h3>
            <p className="mt-2 text-white/90">
              Wir melden uns innerhalb von 2 Werktagen bei dir. Falls es eilig ist: eine kurze Mail an{' '}
              <a className="underline" href="mailto:amirtharajah.stefan@gmail.com">
                amirtharajah.stefan@gmail.com
              </a>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid-border" data-testid="b2b-form">
            <FormField
              icon={<Buildings size={16} />}
              label="Firma *"
              testId="b2b-company"
              value={form.company}
              onChange={(v) => upd('company', v)}
            />
            <FormField
              icon={<UserCircle size={16} />}
              label="Ansprechpartner*in *"
              testId="b2b-contact-name"
              value={form.contact_name}
              onChange={(v) => upd('contact_name', v)}
            />
            <FormField
              icon={<EnvelopeSimple size={16} />}
              label="E-Mail *"
              type="email"
              testId="b2b-email"
              value={form.email}
              onChange={(v) => upd('email', v)}
            />
            <FormField
              icon={<Phone size={16} />}
              label="Telefon (optional)"
              testId="b2b-phone"
              value={form.phone}
              onChange={(v) => upd('phone', v)}
            />
            <div className="p-5 border-b border-neutral-200">
              <label className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1.5">
                <Buildings size={16} /> Firmengröße
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => upd('company_size', s)}
                    data-testid={`b2b-size-${s}`}
                    className={`h-10 px-3 border text-xs uppercase tracking-widest ${
                      form.company_size === s
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-300 hover:border-neutral-900'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-5">
              <label className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1.5">
                <ChatCircle size={16} /> Nachricht (optional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => upd('message', e.target.value)}
                data-testid="b2b-message"
                rows={4}
                className="w-full mt-2 border border-neutral-300 p-3 outline-none focus:border-neutral-900"
                placeholder="Kurz zu deinem Use-Case oder deiner Karriereseite…"
              />
            </div>
            {error && (
              <div className="p-4 bg-red-50 text-red-800 text-sm" data-testid="b2b-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="b2b-submit"
              className="w-full h-16 bg-neutral-900 text-white text-sm uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading ? 'Wird gesendet…' : 'Unverbindlich anfragen'}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function Benefit({ num, title, text }) {
  return (
    <div className="p-8 border-neutral-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r">
      <div className="font-mono text-xs text-neutral-500">{num}</div>
      <div className="font-display text-2xl mt-4">{title}</div>
      <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{text}</p>
    </div>
  );
}

function FormField({ icon, label, value, onChange, testId, type = 'text' }) {
  return (
    <div className="p-5 border-b border-neutral-200">
      <label className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1.5">
        <span>{icon}</span> {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full mt-2 text-2xl font-display bg-transparent outline-none"
      />
    </div>
  );
}
